import { useState, useEffect, useCallback } from 'react';
import type { CartItem } from '../types';
import { getVariantStock, getProductByIdFromCache } from '../utils/productUtils';

export function useCartState() {
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('clothybazar_cart');
      return saved ? (JSON.parse(saved) as CartItem[]) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Sync cart with localStorage — save without images to avoid quota issues
  useEffect(() => {
    try {
      const cartWithoutImages = cart.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        originalPrice: item.originalPrice,
        image: '',
        category: item.category,
        quantity: item.quantity,
        size: item.size,
        selectedColor: item.selectedColor,
      }));
      localStorage.setItem('clothybazar_cart', JSON.stringify(cartWithoutImages));
    } catch {
      try {
        localStorage.removeItem('clothybazar_cart');
        localStorage.setItem('clothybazar_cart', JSON.stringify([]));
      } catch { /* ignore */ }
    }
  }, [cart]);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const showToastMsg = useCallback((msg: string) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  const addItemToCart = useCallback((item: CartItem, maxStock: number) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(
        i => i.id === item.id && i.size === item.size && i.selectedColor?.name === item.selectedColor?.name
      );

      if (existingItem) {
        if (existingItem.quantity >= maxStock) {
          showToastMsg(`Only ${maxStock} item(s) available in stock!`);
          return prevCart;
        }
        return prevCart.map(i =>
          i.id === item.id && i.size === item.size && i.selectedColor?.name === item.selectedColor?.name
            ? { ...i, quantity: Math.min(i.quantity + 1, maxStock) }
            : i
        );
      }

      if (maxStock <= 0) {
        showToastMsg('This item is out of stock!');
        return prevCart;
      }

      showToastMsg(`${item.name}${item.selectedColor ? ` (${item.selectedColor.name})` : ''}${item.size ? ` (${item.size})` : ''} added to cart!`);
      return [...prevCart, { ...item, quantity: 1 }];
    });
  }, [showToastMsg]);

  const removeFromCart = useCallback((productId: number, size?: string) => {
    setCart(prevCart => prevCart.filter(item => !(item.id === productId && item.size === size)));
  }, []);

  const updateQuantity = useCallback((productId: number, size: string | undefined, delta: number) => {
    setCart(prevCart =>
      prevCart.map(item => {
        if (item.id === productId && item.size === size) {
          const product = getProductByIdFromCache(item.id);
          let maxStock = Infinity;

          if (product?.stockBreakdown) {
            maxStock = getVariantStock(
              product.stockBreakdown,
              product.colors ?? [],
              product.sizes,
              item.selectedColor?.name,
              size
            );
          } else if (product?.stock !== undefined && product.stock !== null) {
            maxStock = product.stock;
          }

          const newQuantity = Math.max(1, Math.min(item.quantity + delta, maxStock));
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  return {
    cart,
    setCart,
    isCartOpen,
    setIsCartOpen,
    showToast,
    toastMessage,
    cartTotal,
    cartCount,
    addItemToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    showToastMsg,
  };
}
